import { UserDoc } from '@gooduncles/gu-app-schema';
import { SelectionChangedEvent } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { ColDef } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Input } from 'antd';
import { FC, useCallback, useRef } from 'react';
import { getServiceVersionLabel } from 'src/constants';
import { storeCategories, storeStateList } from 'src/constants/store.constants';

import { AgCellStoreState } from 'src/atomic-components/atoms/AgCell/AgCellStoreState';
import ServiceVersionTag from 'src/atomic-components/atoms/ServiceVersionTag';
import AgGridSettingsButtons from 'src/atomic-components/molecules/AgGridSettingsButtons/AgGridSettingsButtons';

import classes from './KakaoAlimtalkSend.module.scss';

/** 유효한 휴대폰 번호인지 확인 (01x로 시작하는 번호) */
export const isValidMobileNumber = (tel: string): boolean => {
  return /^01[016789]\d{7,8}$/.test(tel.replace(/\D/g, ''));
};

export type UserWithStore = UserDoc & {
  storeName: string;
  storeNickname: string;
  storeCode: string;
  storeState: string;
  storeType: string;
  storeAddress: string;
  storeCategories: string[];
  ownerTel: string;
  latestOrderAt: string | null;
  businessName: string;
};

interface UserSelectionTableProps {
  rowData: UserWithStore[];
  onSelectionChanged: (selectedUsers: UserWithStore[]) => void;
}

const KAKAO_ALIMTALK_USER_TABLE_ID = 'kakao-alimtalk-user-table';

const isRowSelectable = (params: { data?: UserWithStore }): boolean => {
  return isValidMobileNumber(params.data?.userTel ?? '');
};

const defaultColDef: ColDef<UserWithStore> = {
  sortable: true,
  resizable: true,
  filter: true,
};

const columnDefs: ColDef<UserWithStore>[] = [
  {
    headerCheckboxSelection: true,
    checkboxSelection: true,
    width: 50,
    maxWidth: 50,
    suppressMenu: true,
    sortable: false,
    filter: false,
    resizable: false,
  },
  {
    headerName: '앱 버전',
    field: 'serviceVersion',
    width: 90,
    minWidth: 80,
    valueFormatter: (params) => getServiceVersionLabel(params.value),
    cellRenderer: (params: { value: number | undefined }) => <ServiceVersionTag serviceVersion={params.value} />,
  },
  {
    headerName: '매장명(관리용)',
    field: 'storeNickname',
    width: 160,
    minWidth: 120,
  },
  {
    headerName: '연락처',
    field: 'userTel',
    width: 150,
    minWidth: 120,
  },
  {
    headerName: '점주연락처',
    field: 'ownerTel',
    width: 150,
    minWidth: 120,
  },
  {
    headerName: '매장상태',
    field: 'storeState',
    width: 100,
    minWidth: 80,
    cellRenderer: AgCellStoreState,
    filterParams: {
      valueFormatter: (params: { value: string }) =>
        storeStateList[params.value as keyof typeof storeStateList] ?? params.value,
    },
  },
  {
    headerName: '매장코드',
    field: 'storeCode',
    width: 120,
    minWidth: 100,
  },
  {
    headerName: '매장명(고객입력)',
    field: 'storeName',
    width: 160,
    minWidth: 120,
  },
  {
    headerName: '이메일',
    field: 'email',
    width: 200,
    minWidth: 150,
  },
  {
    headerName: '주소',
    field: 'storeAddress',
    width: 250,
    minWidth: 150,
  },
  {
    headerName: '카테고리',
    width: 150,
    minWidth: 100,
    valueGetter: (params) =>
      params.data?.storeCategories?.map((v) => storeCategories[v as keyof typeof storeCategories] ?? v).join(', ') ??
      '',
  },
  {
    headerName: '최근주문일',
    field: 'latestOrderAt',
    width: 140,
    minWidth: 110,
  },
  {
    headerName: '사업자명',
    field: 'businessName',
    width: 150,
    minWidth: 100,
  },
];

const UserSelectionTable: FC<UserSelectionTableProps> = ({ rowData, onSelectionChanged }) => {
  const gridRef = useRef<AgGridReact<UserWithStore>>(null);

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<UserWithStore>) => {
      const selectedRows = event.api.getSelectedRows();
      onSelectionChanged(selectedRows);
    },
    [onSelectionChanged],
  );

  const handleQuickFilter = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    gridRef.current?.api?.setQuickFilter(e.target.value);
  }, []);

  return (
    <div className={classes.tableSection}>
      <div className={classes.tableToolbar}>
        <Input
          placeholder='매장코드, 매장명, 이메일, 연락처로 검색'
          onChange={handleQuickFilter}
          className={classes.quickFilter}
        />
        <AgGridSettingsButtons gridRef={gridRef.current} tableId={KAKAO_ALIMTALK_USER_TABLE_ID} size='small' />
      </div>
      <div className='ag-theme-alpine' style={{ flex: 1, width: '100%' }}>
        <AgGridReact<UserWithStore>
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection='multiple'
          suppressRowClickSelection
          onSelectionChanged={handleSelectionChanged}
          isRowSelectable={isRowSelectable}
          getRowId={(params) => params.data._id}
        />
      </div>
    </div>
  );
};

export default UserSelectionTable;
